import React, { useEffect, useState, useContext, useCallback } from 'react';
// import axios from 'axios';
import {
    RadialBarChart,
    RadialBar,
    PolarAngleAxis,
    LineChart,
    Line,
    XAxis,
    YAxis,    
    Tooltip,
    ResponsiveContainer
} from 'recharts';
import './FearAndGreedDashboard.css';
import TextBaloon from './TextBaloon'; 
import { CsrfTokenContext } from '../CsrfTokenContext';
import { isTokenExpired, handleLogin } from '../pages/utils/AuthenticationHelper';
    
const FearAndGreedDashboard = ({ diameter = 400, coverPosition = -30, showDetails = false }) => {
    const [currentValue, setCurrentValue] = useState(0);
    const [data, setData] = useState([]);    
    const [showChart, setShowChart] = useState(false);    
    const gradientId = `colorGradient-${diameter}`;
    const tickInterval = Math.floor(data.length / 6);
    const csrfToken = useContext(CsrfTokenContext);    

    const refreshTokenIfNeeded = useCallback(async () => {
        if (isTokenExpired()) {        
          await handleLogin();
        }    
      },[]);

    useEffect(() => {
        const fetchFearAndGreedIndex = async () => {
            try {
                await refreshTokenIfNeeded();                
                                             
                const response = await fetch(`/api/fearAndGreed`, {
                    method: 'GET',
                    // credentials: 'include',
                    headers: {                        
                        'Content-Type': 'application/json',
                        'X-CSRFToken': csrfToken // Make sure csrfToken is defined and valid
                    }
                });
        
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
        
                const data = await response.json(); // Convert response to JSON
                
                // in case the first service call fails, the second one is piked but no chart will be displaed since it's not a history list
                setShowChart(data.length > 1);
                      
                // Assuming data is in the format { data: [...] }
                const indexData = data.map(item => ({
                    date: item.timestamp * 1000, // Convert to milliseconds
                    value: parseInt(item.value, 10)
                }));
                   

                setData(indexData); // Update your state or variable with the processed data
                
                // Ensure to get the most recent value
                const latestData = indexData[0];           

                setCurrentValue(latestData ? latestData.value : null);
                
            } catch (error) {
                console.error('Error fetching the Fear & Greed Index data:', error);                
            }
        };

        fetchFearAndGreedIndex();
    }, [csrfToken, refreshTokenIfNeeded]);
    
    // const clampValue = Math.max(0, Math.min(currentValue, 100)); // Ensure value is between 0 and 100
    const clampValue = currentValue ? Math.max(0, Math.min(currentValue, 100)) : Math.floor(Math.random() * (60 - 40 + 1)) + 40;

    let fearLevel = '';
    let fearDescription = '';
    let ballonColor = '';
    let textColor = '';

    // Calculate fear level and description
    if (clampValue >= 0 && clampValue <= 20) {
        fearLevel = 'Extreme Fear';
        fearDescription = 'This level indicates that investors are extremely fearful. It suggests that the market might be oversold, meaning that prices have dropped significantly due to widespread fear. According to contrarian investing principles, this could be a good buying opportunity..';
        ballonColor = '#ff3e15';
        textColor = '#dadada';
    } else if (clampValue >= 21 && clampValue <= 40) {
        fearLevel = 'Fear';
        fearDescription = 'This range indicates that fear is still prevalent but not as extreme as in the lower range. It often suggests caution among investors, with sentiment leaning towards negative or bearish expectations.';
        ballonColor = '#ff5f1d';
        textColor = '#dadada';
    } else if (clampValue >= 41 && clampValue <= 60) {
        fearLevel = 'Neutral';
        fearDescription = 'This range indicates a balanced sentiment in the market. Investors are neither overly fearful nor excessively greedy, reflecting a stable market environment with no strong bias towards either buying or selling.';
        ballonColor = '#ffcb16';
        textColor = '#003c5a';
    } else if (clampValue >= 61 && clampValue <= 80) {
        fearLevel = 'Greed';
        fearDescription = 'Greed begins to dominate market sentiment in this range. Investors are optimistic and confident, often expecting further price increases. This may signal a time when caution is warranted, as markets could be overbought.';
        ballonColor = '#96c708';
        textColor = '#003c5a';
    } else if (clampValue >= 81 && clampValue <= 100) {
        fearLevel = 'Extreme Greed';
        fearDescription = 'This level reflects extreme optimism and greed among investors. It often indicates an overbought market condition where prices may be unsustainable. Investors may consider taking profits or exercising caution.';
        ballonColor = '#4aa006';
        textColor = '#dadada';
    }

    const formatDate = (date) => {
        const result = new Date(date);
        const year = result.getFullYear();
        const month = String(result.getMonth() + 1).padStart(2, '0'); // Months are zero-based
        const day = String(result.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Calculate the position of the arrow
    const calculateArrowPosition = (value, radius) => {
        const angle = (180 - (value / 100) * 180) * (Math.PI / 180); // Convert to radians
        const x = radius * Math.cos(angle);
        const y = radius * Math.sin(angle);
        return { x, y, angle: 180 - (value / 100) * 180 }; // Calculate angle for rotation
    };

    const CustomTooltip = ({ active = false, payload = [] }) => {
        if (active && payload && payload.length) {
          const data = payload[0].payload;
          
          let tooltipText = '';
          let tooltipColor = '';
          if (data.value >= 0 && data.value <= 20) {
            tooltipText = 'Extreme Fear';
            tooltipColor = '#ff3e15';
          } else if (data.value >= 21 && data.value <= 40) {
            tooltipText = 'Fear';
            tooltipColor = '#ff5f1d';
          } else if (data.value >= 41 && data.value <= 60) {
            tooltipText = 'Neutral';
            tooltipColor = '#ffcb16';
          } else if (data.value >= 61 && data.value <= 80) {
            tooltipText = 'Greed';
            tooltipColor = '#96c708';
          } else if (data.value >= 81 && data.value <= 100) {
            tooltipText = 'Extreme Greed';
            tooltipColor = '#4aa006';
          }
          
          return (
            <div className="custom-tooltip">
              <div style={{ color: tooltipColor, textShadow:'-1px -1px 0 #131722, 1px -1px 0 #131722, -1px 1px 0 #131722, 1px 1px 0 #131722' }}>{tooltipText}</div>
              <div>{formatDate(new Date(data.date).toLocaleDateString())}</div>
              <div>Index : {data.value}</div>
            </div>
          );
        }
        
        return null;
      };

    const arrowPosition = calculateArrowPosition(clampValue, diameter / 2 - 50); // Adjust radius as needed

    return (
        <>        
            {data.length > 0 && (
                <div className="dashboard">            
                    <div className="gauge-container">
                        <RadialBarChart
                            width={diameter}
                            height={diameter / 2}
                            cx={diameter / 2}
                            cy={diameter / 2}
                            innerRadius={diameter / 2 - 50}
                            outerRadius={diameter / 2 - 10}
                            barSize={20}
                            startAngle={180}
                            endAngle={0}
                            data={[{ name: 'Index', value: 100 }]}
                        >
                            <defs>                                                                
                                <linearGradient id={gradientId} gradientUnits="userSpaceOnUse" x1="0%" y1="0%" x2='100%' y2="0%">                                
                                    <stop offset="0%" stopColor="red" />
                                    <stop offset="50%" stopColor="yellow" />
                                    <stop offset="100%" stopColor="green" />
                                </linearGradient>
                            </defs>
                            <PolarAngleAxis
                                type="number"
                                domain={[0, 100]}
                                angleAxisId={0}
                                tick={false}
                            />
                            <RadialBar
                                minAngle={15}
                                clockWise
                                dataKey="value"
                                fill={`url(#${gradientId})`}
                            />
                            <text
                                x={diameter / 2}
                                y={diameter / 2 + coverPosition + 20}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                className="progress-label"
                                style={{ fontSize: showDetails ? '35px' : fearLevel.includes('Extreme') ? '25px' : '30px', fontWeight: 'bold', fill: '#b3b3b3' }}
                            >                              
                                {fearLevel}
                            </text>
                            <svg
                                x={0}
                                y={0}
                                width={diameter}
                                height={diameter / 2}
                                viewBox={`0 0 ${diameter} ${diameter / 2}`}
                                style={{ overflow: 'visible' }}
                            >
                                <circle
                                    cx={diameter / 2 + arrowPosition.x}
                                    cy={diameter / 2 - arrowPosition.y}
                                    r="20"
                                    fill="white"
                                />
                                <text
                                    x={diameter / 2 + arrowPosition.x}
                                    y={diameter / 2 - arrowPosition.y}
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    fill="black"
                                    fontSize="30px"
                                    fontWeight="bold"
                                >
                                    {clampValue}
                                </text>
                            </svg>                    
                        </RadialBarChart>                
                    </div>
                    {showDetails && (
                    <>
                        {/* <div style={{ textAlign: 'justify', marginTop: '10px', color: '#b3b3b3', fontSize: '16px' }}>
                            {fearDescription}
                        </div> */}
                        <br/><br/>
                        <TextBaloon number="" arrowPosition ={'top'} textColor={textColor} text={<>{fearDescription}</>} color={ballonColor} />                    
                        <br/><br/>                        
                        {showChart &&
                        <><h1>Last 6 Months</h1>
                        <ResponsiveContainer width="100%" height={300}>
                                    <LineChart
                                        data={data}
                                        margin={{
                                            top: 5, right: 30, left: 20, bottom: 5,
                                        }}
                                    >
                                        <XAxis
                                            dataKey="date"
                                            tickFormatter={(date) => formatDate(new Date(date).toLocaleDateString())}
                                            tick={{ angle: 0, textAnchor: 'end', dx: -10, dy: 5 }}
                                            interval={tickInterval} />
                                        <YAxis />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Line
                                            type="linear"
                                            dataKey="value"
                                            stroke="#8884d8"
                                            strokeWidth={2}
                                            dot={false}
                                            name='Index' />
                                    </LineChart>
                                </ResponsiveContainer></>
                    }
                    </>
                    )}
                </div>
            )}
        </>
    );    
};

export default FearAndGreedDashboard;